<script>
import { GlBadge, GlIcon, GlTooltipDirective } from '@gitlab/ui';
import { GITLAB_COM_PLAN_TYPE } from '~/javascripts/subscription/constants';
import { isFutureDate, toLocaleDateString } from '~/javascripts/lib/datetime';
import { getPlanDateText, getDateTooltip } from './utils';
import GroupDetails from './group_details.vue';

export default {
  name: 'GroupDetailsHeader',
  components: {
    GlBadge,
    GroupDetails,
    GlIcon,
  },
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  inject: ['subscription'],
  computed: {
    dateText() {
      return getPlanDateText({
        willAutoRenew: this.willAutoRenew,
        hasExpired: this.hasExpired,
        hasFutureStartDate: this.hasFutureStart,
        endDateLocale: this.endDateLocale,
        startDateLocale: this.startDateLocale,
      });
    },
    hasExpired() {
      return Boolean(this.subscription.endDate && !isFutureDate(this.subscription.endDate));
    },
    hasFutureStart() {
      return Boolean(this.subscription.startDate && isFutureDate(this.subscription.startDate));
    },
    endDateLocale() {
      return this.subscription.endDate ? toLocaleDateString(this.subscription.endDate) : null;
    },
    startDateLocale() {
      return this.subscription.startDate ? toLocaleDateString(this.subscription.startDate) : null;
    },
    willAutoRenew() {
      return this.subscription.autoRenew && !this.subscription.preventAutoRenew;
    },
    tooltipContent() {
      return getDateTooltip({
        startDate: this.startDateLocale,
        endDate: this.endDateLocale,
        renewalExpirationDate: this.renewalExpirationLocale,
        willAutoRenew: this.willAutoRenew,
        hasExpired: this.hasExpired,
        hasFutureStart: this.hasFutureStart,
      });
    },
    renewalExpirationLocale() {
      return this.subscription.renewalExpirationDate
        ? toLocaleDateString(this.subscription.renewalExpirationDate)
        : null;
    },
    isSaaS() {
      return this.subscription.type === GITLAB_COM_PLAN_TYPE;
    },
    temporaryExtensionText() {
      return `Access temporarily extended until ${toLocaleDateString(
        this.subscription.temporaryExtensionEndDate,
      )}`;
    },
    temporaryExtensionRecentlyExpiredText() {
      return `Temporary extension expired on ${toLocaleDateString(
        this.subscription.recentlyExpiredTemporaryExtensionEndDate,
      )}`;
    },
  },
};
</script>

<template>
  <div>
    <div class="gl-text-gray-600">
      <group-details
        v-if="isSaaS"
        class="gl-mb-2"
        :namespace="subscription.namespace"
        :namespace-details="subscription.namespaceDetails"
        :subscription-name="subscription.name"
      />
      <div v-if="dateText" data-testid="date" class="gl-flex gl-items-center gl-gap-2">
        {{ dateText }}
        <gl-icon
          v-gl-tooltip.right
          :title="tooltipContent"
          :aria-label="tooltipContent"
          data-testid="date-information"
          name="information"
          variant="subtle"
          role="tooltip"
        />
        <gl-badge
          v-if="subscription.temporaryExtensionEndDate"
          variant="info"
          class="gl-ml-3"
          data-testid="temporary-extension-badge"
        >
          {{ temporaryExtensionText }}
        </gl-badge>
        <gl-badge
          v-if="subscription.recentlyExpiredTemporaryExtensionEndDate"
          variant="info"
          data-testid="recently-expired-temporary-extension-badge"
        >
          {{ temporaryExtensionRecentlyExpiredText }}
        </gl-badge>
      </div>
    </div>
  </div>
</template>
